"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import type { Route } from "next";
import { usePathname, useRouter } from "next/navigation";
import {
  Repeat,
  Workflow,
  Sparkles,
  ShieldCheck,
  LogOut,
  Plus,
  UserRound,
  Ellipsis,
  Pencil,
  Trash2,
  FileText,
  FileDown,
  Share2,
  PanelLeftClose,
  PanelLeftOpen
} from "lucide-react";
import { useAuthStore } from "@/stores/auth-store";
import { useAgentStore } from "@/stores/agent-store";
import { useUiStore } from "@/stores/ui-store";
import { api } from "@/services/client";
import type { ChatSession } from "@/services/contracts/types";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

type NavItem = {
  href: Route;
  label: string;
  icon: typeof Repeat;
  adminOnly?: boolean;
};

const nav: NavItem[] = [
  { href: "/app/rituals", label: "Rituals", icon: Repeat },
  { href: "/app/workflows", label: "Workflows", icon: Workflow },
  { href: "/app/skills" as Route, label: "Skills", icon: Sparkles },
  { href: "/app/admin/users", label: "Admin", icon: ShieldCheck, adminOnly: true }
] as const;

function toTimestamp(value: string): number {
  const timestamp = Date.parse(value);
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function sortSessionsNewestFirst(list: ChatSession[]): ChatSession[] {
  return [...list].sort((a, b) => {
    // Sort by updatedAt descending (newest first)
    const updatedDiff = toTimestamp(b.updatedAt) - toTimestamp(a.updatedAt);
    if (updatedDiff !== 0) return updatedDiff;

    // Then by createdAt descending (newest first)
    const createdDiff = toTimestamp(b.createdAt) - toTimestamp(a.createdAt);
    if (createdDiff !== 0) return createdDiff;

    // Finally by id descending
    return b.id.localeCompare(a.id);
  });
}

function mergeSessionsPreservingLocalRecency(previous: ChatSession[], incoming: ChatSession[]): ChatSession[] {
  const previousById = new Map(previous.map((session) => [session.id, session]));
  return incoming.map((session) => {
    const existing = previousById.get(session.id);
    if (!existing) return session;

    const incomingUpdatedAt = toTimestamp(session.updatedAt);
    const existingUpdatedAt = toTimestamp(existing.updatedAt);
    const keepExistingRecency = existingUpdatedAt > incomingUpdatedAt;

    return {
      ...session,
      title: session.title === "New chat" && existing.title !== "New chat" ? existing.title : session.title,
      createdAt: keepExistingRecency ? existing.createdAt : session.createdAt,
      updatedAt: keepExistingRecency ? existing.updatedAt : session.updatedAt
    };
  });
}

export function AppShell({ children }: { children: React.ReactNode }) {
  const path = usePathname();
  const router = useRouter();
  const { role, username, signOut } = useAuthStore();
  const selectedAgent = useAgentStore((s) => s.selectedAgent);
  const agentHydrated = useAgentStore((s) => s.hasHydrated);
  const artifactsEnabled = useUiStore((s) => s.artifactsEnabled);
  const setArtifactsEnabled = useUiStore((s) => s.setArtifactsEnabled);
  const artifactCountsBySession = useUiStore((s) => s.artifactCountsBySession);
  const [sessions, setSessions] = useState<ChatSession[]>([]);
  const [menuSessionId, setMenuSessionId] = useState<string | null>(null);
  const [busySessionId, setBusySessionId] = useState<string | null>(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const activeChatId = useMemo(() => {
    if (!path.startsWith("/app/chat/")) return null;
    const segment = path.split("/")[3] ?? null;
    if (!segment) return null;
    try {
      return decodeURIComponent(segment);
    } catch {
      return segment;
    }
  }, [path]);

  const activeArtifactCount = activeChatId ? artifactCountsBySession[activeChatId] ?? 0 : 0;
  const showArtifactsToggle = path.startsWith("/app/chat") && activeArtifactCount > 0;

  const refreshSessions = useCallback(async (): Promise<void> => {
    if (!username) {
      setSessions([]);
      return;
    }
    try {
      const data = await api.sessions.listSessions(username);
      setSessions((previous) => mergeSessionsPreservingLocalRecency(previous, data));
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (message.toLowerCase().includes("not authenticated") || message.toLowerCase().includes("unauthorized")) {
        signOut();
        setSessions([]);
        router.replace("/auth/sign-in");
        return;
      }
      console.error("Failed to refresh sessions", error);
    }
  }, [username, router, signOut]);

  useEffect(() => {
    void refreshSessions();
  }, [refreshSessions]);

  const sortedSessions = useMemo(() => sortSessionsNewestFirst(sessions), [sessions]);

  useEffect(() => {
    const handleSessionsUpdated = (): void => {
      void refreshSessions();
    };
    const handleSessionTitleHint = (event: Event): void => {
      const customEvent = event as CustomEvent<{ sessionId?: string; title?: string }>;
      const sessionId = customEvent.detail?.sessionId;
      const hintedTitle = customEvent.detail?.title?.trim();
      if (!sessionId || !hintedTitle) return;
      const now = new Date().toISOString();
      setSessions((previous) =>
        previous.map((session) =>
          session.id === sessionId
            ? {
                ...session,
                title: session.title === "New chat" ? hintedTitle : session.title,
                updatedAt: now
              }
            : session
        )
      );
    };
    window.addEventListener("stella:sessions-updated", handleSessionsUpdated);
    window.addEventListener("stella:session-title-hint", handleSessionTitleHint as EventListener);
    return () => {
      window.removeEventListener("stella:sessions-updated", handleSessionsUpdated);
      window.removeEventListener("stella:session-title-hint", handleSessionTitleHint as EventListener);
    };
  }, [refreshSessions]);

  useEffect(() => {
    if (!showArtifactsToggle && artifactsEnabled) {
      setArtifactsEnabled(false);
    }
  }, [artifactsEnabled, setArtifactsEnabled, showArtifactsToggle]);

  const createSession = async (): Promise<void> => {
    if (!username) return;
    const session = await api.sessions.createSession(username, {
      agentName: agentHydrated ? selectedAgent : "stella"
    });
    setSessions((prev) => [session, ...prev]);
    router.push(`/app/chat/${session.id}`);
  };

  const renameSession = async (session: ChatSession): Promise<void> => {
    const title = window.prompt("Rename chat", session.title)?.trim();
    if (!title || title === session.title) return;
    await api.sessions.renameSession(session.id, title);
    await refreshSessions();
    setMenuSessionId(null);
  };

  const deleteSession = async (session: ChatSession): Promise<void> => {
    const confirmed = window.confirm(`Delete chat "${session.title}"?`);
    if (!confirmed) return;
    if (!username) return;
    await api.sessions.deleteSession(session.id);
    const nextSessions = sortSessionsNewestFirst(await api.sessions.listSessions(username));
    setSessions(nextSessions);
    setMenuSessionId(null);

    if (activeChatId === session.id) {
      if (nextSessions[0]) {
        router.push(`/app/chat/${nextSessions[0].id}`);
      } else {
        const created = await api.sessions.createSession(username, {
          agentName: agentHydrated ? selectedAgent : "stella"
        });
        setSessions([created]);
        router.push(`/app/chat/${created.id}`);
      }
    }
  };

  const downloadBlob = (blob: Blob, fileName: string): void => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const buildExportFileStem = (session: ChatSession): string => {
    const safeTitle = (session.title || "chat").replace(/[^a-z0-9-_]+/gi, "_").toLowerCase();
    const safeId = session.id.replace(/[^a-z0-9-_]+/gi, "_").toLowerCase();
    return `${safeTitle}_${safeId}`;
  };

  const shareSession = async (session: ChatSession): Promise<void> => {
    setBusySessionId(session.id);
    try {
      const uuid = await api.sessions.shareSession(session.id);
      const shareUrl = `${window.location.origin}/share/${encodeURIComponent(uuid)}`;
      try {
        await navigator.clipboard.writeText(shareUrl);
      } catch {
        window.prompt("Copy share link", shareUrl);
      }
    } finally {
      setMenuSessionId(null);
      setBusySessionId(null);
    }
  };

  const exportSessionMarkdown = async (session: ChatSession): Promise<void> => {
    setBusySessionId(session.id);
    try {
      const markdown = await api.sessions.exportSessionMarkdown(session.id);
      downloadBlob(new Blob([markdown], { type: "text/markdown;charset=utf-8" }), `${buildExportFileStem(session)}.md`);
    } finally {
      setMenuSessionId(null);
      setBusySessionId(null);
    }
  };

  const exportSessionPdf = async (session: ChatSession): Promise<void> => {
    setBusySessionId(session.id);
    try {
      const pdfBlob = await api.sessions.exportSessionPdf(session.id);
      downloadBlob(pdfBlob, `${buildExportFileStem(session)}.pdf`);
    } finally {
      setMenuSessionId(null);
      setBusySessionId(null);
    }
  };

  const visibleNavItems = nav.filter((item) => !item.adminOnly || role === "admin");

  const handleSignOut = (): void => {
    signOut();
    router.push("/auth/sign-in");
  };

  return (
    <div className="app-canvas relative z-10 text-[var(--text)]">
      <div className="h-full w-full p-2 md:p-3">
        <div className="app-window flex h-full w-full">
          <aside
            className={cn(
              "theme-sidebar relative shrink-0 overflow-hidden transition-[width,padding,opacity] duration-300 flex flex-col",
              sidebarOpen ? "w-[272px] p-3 opacity-100" : "w-0 p-0 opacity-0"
            )}
          >
            <button
              type="button"
              className="theme-nav-item absolute right-3 top-3 z-20 inline-flex h-8 w-8 items-center justify-center text-[var(--ui-text-soft)]"
              onClick={() => setSidebarOpen(false)}
              aria-label="Close sidebar"
            >
              <PanelLeftClose size={15} />
            </button>

            <div className="shrink-0 space-y-4">
              <div className="flex justify-center pb-1 pt-1">
                <Image
                  src="/stella-logo.png"
                  alt="Stella logo"
                  width={58}
                  height={58}
                  priority
                  className="h-[58px] w-[58px] object-contain"
                />
              </div>

              <button
                onClick={() => void createSession()}
                className="multiverse-btn flex h-11 w-full items-center gap-2.5 px-3 py-2 text-sm font-medium"
              >
                <Plus size={15} />
                New chat
              </button>

              <nav className="flex flex-col gap-1">
                {visibleNavItems.map((item) => {
                    const Icon = item.icon;
                    const active = path === item.href || path.startsWith(`${item.href}/`);
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className={cn(
                          "theme-nav-item flex items-center gap-2.5 px-3 py-2 text-sm",
                          active ? "theme-nav-item-active" : "text-[var(--ui-text-muted)]"
                        )}
                      >
                        <Icon size={16} />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
              </nav>

              {showArtifactsToggle ? (
                <button
                  type="button"
                  className={cn(
                    "theme-nav-item mt-1 flex w-full items-center justify-between gap-2.5 px-3 py-2 text-sm",
                    artifactsEnabled
                      ? "theme-nav-item-active"
                      : "text-[var(--ui-text-muted)]"
                  )}
                  onClick={() => setArtifactsEnabled(!artifactsEnabled)}
                  aria-pressed={artifactsEnabled}
                  aria-label={artifactsEnabled ? "Hide artifacts panel" : "Show artifacts panel"}
                >
                  <span className="inline-flex items-center gap-2.5">
                    <FileText size={16} />
                    <span>Artifacts</span>
                  </span>
                  <span className="tech-label text-[10px] text-[var(--ui-text-soft)]">{activeArtifactCount}</span>
                </button>
              ) : null}
            </div>

            <div className="theme-divider min-h-0 flex-1 border-t pt-3 mt-4 flex flex-col">
              <div className="mb-2 flex items-center justify-between px-1">
                <p className="tech-label text-xs text-[var(--ui-text-soft)]">Chats</p>
                <button className="theme-nav-item px-2 py-1 text-xs text-[var(--ui-text-muted)]" onClick={() => void refreshSessions()}>
                  Refresh
                </button>
              </div>

              <div className="min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
                  {sortedSessions.map((session) => {
                    const active = activeChatId === session.id;
                    return (
                      <div
                        key={session.id}
                        className={cn(
                          "theme-nav-item group flex items-center gap-2 px-2 py-1.5 text-sm",
                          active ? "theme-nav-item-active" : "text-[var(--ui-text-muted)]"
                        )}
                      >
                        <Link href={`/app/chat/${session.id}`} className="min-w-0 flex-1 px-1 py-1">
                          <p className="truncate">{session.title}</p>
                        </Link>
                        <Popover open={menuSessionId === session.id} onOpenChange={(open) => setMenuSessionId(open ? session.id : null)}>
                          <PopoverTrigger asChild>
                            <button
                              type="button"
                              className={cn(
                                "inline-flex h-7 w-7 items-center justify-center rounded-md text-[var(--ui-text-soft)] transition hover:bg-[var(--ui-hover-bg)] hover:text-[var(--text)]",
                                menuSessionId === session.id
                                  ? "opacity-100 pointer-events-auto"
                                  : "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
                              )}
                              aria-label={`Actions for ${session.title}`}
                            >
                              <Ellipsis size={14} />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent align="end" side="right" className="w-40 p-1">
                            <button
                              type="button"
                              className="theme-nav-item flex w-full items-center gap-2 px-2.5 py-2 text-xs text-[var(--ui-text-muted)]"
                              onClick={() => void shareSession(session)}
                              disabled={busySessionId === session.id}
                            >
                              <Share2 size={13} />
                              Share Link
                            </button>
                            <button
                              type="button"
                              className="theme-nav-item flex w-full items-center gap-2 px-2.5 py-2 text-xs text-[var(--ui-text-muted)]"
                              onClick={() => void exportSessionMarkdown(session)}
                              disabled={busySessionId === session.id}
                            >
                              <FileText size={13} />
                              Export Markdown
                            </button>
                            <button
                              type="button"
                              className="theme-nav-item flex w-full items-center gap-2 px-2.5 py-2 text-xs text-[var(--ui-text-muted)]"
                              onClick={() => void exportSessionPdf(session)}
                              disabled={busySessionId === session.id}
                            >
                              <FileDown size={13} />
                              Export PDF
                            </button>
                            <button
                              type="button"
                              className="theme-nav-item flex w-full items-center gap-2 px-2.5 py-2 text-xs text-[var(--ui-text-muted)]"
                              onClick={() => void renameSession(session)}
                            >
                              <Pencil size={13} />
                              Rename
                            </button>
                            <button
                              type="button"
                              className="theme-nav-item flex w-full items-center gap-2 px-2.5 py-2 text-xs text-[var(--ui-text-muted)]"
                              onClick={() => void deleteSession(session)}
                            >
                              <Trash2 size={13} />
                              Delete
                            </button>
                          </PopoverContent>
                        </Popover>
                      </div>
                    );
                  })}
                {!sortedSessions.length ? <p className="px-2 py-1 text-xs text-[var(--ui-text-soft)]">No chats yet.</p> : null}
              </div>
            </div>

            <div className="theme-divider shrink-0 border-t pt-3 mt-3">
              <Link
                href="/app/profile"
                className="theme-nav-item flex items-center justify-center gap-2 px-3 py-2 text-sm text-[var(--ui-text-muted)]"
              >
                <UserRound size={15} />
                <span className="truncate">{username ?? "guest"}</span>
              </Link>
              <button
                className="theme-nav-item mt-3 flex w-full items-center justify-center gap-2 px-3 py-2 text-sm text-[var(--ui-text-muted)]"
                onClick={handleSignOut}
              >
                <LogOut size={14} />
                Sign out
              </button>
            </div>
          </aside>

          <section className="app-main relative min-h-0 flex-1 overflow-hidden">
            {!sidebarOpen ? (
              <div className="absolute left-4 top-4 z-30">
                <div className="theme-panel-soft flex flex-col items-center gap-2 rounded-2xl px-2 py-2 shadow-lg backdrop-blur">
                  <button
                    type="button"
                    className="theme-nav-item group relative inline-flex h-10 w-10 items-center justify-center text-[var(--ui-text-soft)]"
                    onClick={() => setSidebarOpen(true)}
                    aria-label="Open sidebar"
                  >
                    <PanelLeftOpen size={16} />
                    <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[var(--surface)] px-2 py-1 text-xs text-[var(--text)] opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100">
                      Expand
                    </span>
                  </button>

                  <button
                    type="button"
                    className="theme-nav-item group relative inline-flex h-10 w-10 items-center justify-center text-[var(--ui-text-muted)]"
                    onClick={() => void createSession()}
                    aria-label="New chat"
                  >
                    <Plus size={16} />
                    <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[var(--surface)] px-2 py-1 text-xs text-[var(--text)] opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100">
                      New chat
                    </span>
                  </button>

                  {visibleNavItems.map((item) => {
                    const Icon = item.icon;
                    const active = path === item.href || path.startsWith(`${item.href}/`);
                    return (
                      <Link
                        key={`collapsed-${item.href}`}
                        href={item.href}
                        className={cn(
                          "theme-nav-item group relative inline-flex h-10 w-10 items-center justify-center",
                          active ? "theme-nav-item-active" : "text-[var(--ui-text-muted)]"
                        )}
                        aria-label={item.label}
                      >
                        <Icon size={16} />
                        <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[var(--surface)] px-2 py-1 text-xs text-[var(--text)] opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100">
                          {item.label}
                        </span>
                      </Link>
                    );
                  })}

                  {showArtifactsToggle ? (
                    <button
                      type="button"
                      className={cn(
                        "theme-nav-item group relative inline-flex h-10 w-10 items-center justify-center",
                        artifactsEnabled ? "theme-nav-item-active" : "text-[var(--ui-text-muted)]"
                      )}
                      onClick={() => setArtifactsEnabled(!artifactsEnabled)}
                      aria-label={artifactsEnabled ? "Hide artifacts panel" : "Show artifacts panel"}
                    >
                      <FileText size={16} />
                      <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[var(--surface)] px-2 py-1 text-xs text-[var(--text)] opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100">
                        Artifacts ({activeArtifactCount})
                      </span>
                    </button>
                  ) : null}

                  <Link
                    href="/app/profile"
                    className="theme-nav-item group relative inline-flex h-10 w-10 items-center justify-center text-[var(--ui-text-muted)]"
                    aria-label="User profile"
                  >
                    <UserRound size={16} />
                    <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[var(--surface)] px-2 py-1 text-xs text-[var(--text)] opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100">
                      {username ?? "Profile"}
                    </span>
                  </Link>

                  <button
                    type="button"
                    className="theme-nav-item group relative inline-flex h-10 w-10 items-center justify-center text-[var(--ui-text-muted)]"
                    onClick={handleSignOut}
                    aria-label="Sign out"
                  >
                    <LogOut size={16} />
                    <span className="pointer-events-none absolute left-[calc(100%+10px)] top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md bg-[var(--surface)] px-2 py-1 text-xs text-[var(--text)] opacity-0 shadow-md transition-opacity duration-200 group-hover:opacity-100">
                      Sign out
                    </span>
                  </button>
                </div>
              </div>
            ) : null}
          {children}
          </section>
        </div>
      </div>
    </div>
  );
}
